<template>
  <div>
    <Teleport v-for="(item, index) in items" :key="index" :to="item.el">
      <component
        v-if="item.settings"
        :is="componentsList[item.type]"
        :viewSettings="item.settings.view"
        @swipeToMarket="swipeToMarket(item, ANCHOR_TYPES.FRAME)"
      >
        <template
          v-slot:tags
          v-if="item.settings.tags && item.settings.tags.position !== 0"
        >
          <TagsBlock :tags="getTagsList(item.settings.tags, item.idParam)" />
        </template>
        <template v-slot:header v-if="item.settings.header.position !== 0">
          <HeaderBlock
            :text="item.settings.header?.text"
            :isShowAiIcon="item.settings.header.isShowAiIcon"
          >
            <template
              #anchor
              v-if="
                item.settings.additionalAnchor &&
                item.settings.additionalAnchor.position !== 0
              "
            >
              <AnchorBlock
                :text="item.settings.additionalAnchor.text"
                @swipeToMarket="swipeToMarket(item, ANCHOR_TYPES.BUTTON)"
              />
            </template>
            <template
              #[`ai-`+item.settings.ai?.position]
              v-if="[1, 3].includes(item.settings.ai?.position)"
            >
              <AIBlock
                :text="item.settings.ai.text"
                :count="item.settings.ai.count"
                :anchorText="item.settings.ai.anchorText"
                @swipeToMarket="swipeToMarket(item, ANCHOR_TYPES.BUTTON)"
              />
            </template>
            <template
              #faq
              v-if="
                item.settings.faq?.position !== 0 && item.settings.faq?.text
              "
            >
              <FAQBlock :text="item.settings.faq.text" />
            </template>
          </HeaderBlock>
        </template>
        <template #content v-if="item.settings.description?.position === 1">
          <ContentBlock
            :text="getDescription(item.settings.description, item.idParam)"
          />
        </template>
        <template #ai-2 v-if="item.settings.ai?.position === 2">
          <AIBlock
            :text="item.settings.ai.text"
            :count="item.settings.ai.count"
            :anchorText="item.settings.ai.anchorText"
            @swipeToMarket="swipeToMarket(item, ANCHOR_TYPES.BUTTON)"
          />
        </template>
        <template
          #arrow
          v-if="item.settings.arrow && item.settings.view.type !== 1"
        >
          <ArrowBlock
            :direction="item.settings.arrow.position"
            :offset="item.settings.arrow.offset"
          />
        </template>
        <template #logo>
          <LogoBlock :isHideFrisbuyIcon="item.settings.view?.hideBrand" />
        </template>
      </component>
    </Teleport>
    <div v-html="props.settings.html_title_block" />
  </div>
</template>

<script lang="ts" setup>
// Modules
import {
  ref,
  onMounted,
  Teleport,
  shallowRef,
  onUnmounted,
  onBeforeMount,
} from "vue";
import axios, { AxiosResponse } from "axios";
// Components
import SumPopupSection from "../components/SumPopupSection.vue";
import SumSection from "../components/SumSection.vue";
import HeaderBlock from "../components/blocks/HeaderBlock.vue";
import AIBlock from "../components/blocks/AIBlock.vue";
import FAQBlock from "../components/blocks/FAQBlock.vue";
import TagsBlock from "../components/blocks/TagsBlock.vue";
import ContentBlock from "../components/blocks/ContentBlock.vue";
import AnchorBlock from "../components/blocks/AnchorBlock.vue";
import ArrowBlock from "../components/blocks/ArrowBlock.vue";
import LogoBlock from "../components/blocks/LogoBlock.vue";
// Hooks
import { useDebounce } from "../hooks/debounce";
// Types
import {
  ISettings,
  ISummaryList,
  ISummary,
  ITag,
  IItem,
} from "../types/settings";

const props = defineProps<{
  settings: ISettings;
  summaryList: ISummaryList;
}>();

const debouncedGetSummary = useDebounce(getSummaryList, 50);

let mutationObserver: MutationObserver | null = null;
let intersectionObserver: IntersectionObserver | null = null;

const FRSelector = "fsumsection";
const ANCHOR_TYPES = {
  BUTTON: 0,
  FRAME: 2,
};
const widgetTypes = {
  MAIN_TYPE: 0,
  POPUP_TYPE: 1,
};

const summaryList = ref<ISummaryList>();
const waitList = ref<Array<{trackedNode: Element, settings: ISummary, idParam: string, type: string}>>([]);
const componentsList = ref({
  [widgetTypes.MAIN_TYPE]: shallowRef(SumSection),
  [widgetTypes.POPUP_TYPE]: shallowRef(SumPopupSection),
});
const items = ref<Array<IItem>>([]);

onBeforeMount(() => {
  summaryList.value = props.summaryList;
});

onMounted(() => {
  init();

  mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.removedNodes) {
        handleNodeRemoved(node);
      }

      for (const node of mutation.addedNodes) {
        handleNodeAdded(node);
      }
    }
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // intersectionObserver = new IntersectionObserver(
  //   (entries) => {
  //     for (const entry of entries) {
  //       const item = items.value.find((i) => i.el === entry.target);
  //       if (!item) continue;

  //       if (entry.isIntersecting) {
  //         intersectionObserver?.unobserve(entry.target);
  //       }
  //     }
  //   },
  //   { rootMargin: "300px" }
  // );
  // items.value.forEach((item) => intersectionObserver?.observe(item.el));
});

onUnmounted(() => {
  mutationObserver?.disconnect();
  // intersectionObserver?.disconnect();
});

function swipeToMarket(item: IItem, type: number = 0) {
  const realType = typeof type === "number" ? type : 0;
  
  if (!item.settings.anchor) return;
  if (realType !== item.settings.anchor.type) return;
  
  const market = document.querySelector(item.settings.anchor.selector);

  if (!market) return;

  const marketPosition = market.getBoundingClientRect().top + window.pageYOffset;

  window.scrollTo({
    top: marketPosition,
    behavior: "smooth",
  });
}

function getTagsList(tags: ITag, idParam: string) {
  if (!summaryList.value) return;

  const productSummary = summaryList.value[idParam];
  let list: Array<string> = [];

  if (tags.positive && tags.positive.isShow) {
    list = [...productSummary.summary[tags.positive.jsonParam]];
  }

  if (tags.negative && tags.negative.isShow) {
    list = [...list, ...productSummary.summary[tags.negative.jsonParam]];
  }

  return list;
}

function getDescription(description: { jsonParam: "overall_impression"; position: number; }, idParam: string) {
  if (!summaryList.value) return;

  const productSummary = summaryList.value[idParam];

  return productSummary.summary[description.jsonParam];
}

function getSummaryList() {
  const ids = waitList.value.map(summary => `&${summary.type}[]=${summary.idParam}`).join('');

  axios
    .get(`${props.settings.srcDomain}/fb/reviews-summary?embed_id=${props.settings.embedId}${ids}&with_settings=1`)
    .then((response: AxiosResponse<{ settings: ISettings, summaryList: ISummaryList }>) => {
        if (response && response.data.summaryList) {
          Object.keys(response.data.summaryList).forEach((summaryId) => {
            if (!summaryList.value) return;

            summaryList.value[summaryId] = response.data.summaryList[summaryId];
          });
        }
        waitList.value.forEach((waitItem) => {
          elementInit(
            waitItem.trackedNode,
            waitItem.settings,
            waitItem.idParam,
            waitItem.type
          );
        });
      }
    )
    .catch((error) => {
      console.log('error');
      console.log(error);
    });
}

function removeTrackedItem(target: Element, selector: string) {
  if (!selector) {
    return;
  }

  intersectionObserver?.unobserve(target);
  items.value = items.value.filter((item) => {
    return item.el !== target || item.settings.selector !== selector;
  });
}

function handleNodeRemoved(node: Node) {
  if (node.nodeType !== 1) {
    return;
  }

  const element = node as HTMLElement;
  const removeNode = (trackedNode: Element) => {
    if (!(trackedNode instanceof HTMLElement)) {
      return;
    }

    const settings = props.settings.summary.find((summary) => {
      return typeof summary.selector === 'string'
        && trackedNode.classList.contains(summary.selector);
    });

    if (!settings) {
      return;
    }

    removeTrackedItem(trackedNode, settings.selector);
  };

  if (element.nodeName.toLowerCase() === FRSelector) {
    removeNode(element);
  }

  element.querySelectorAll(FRSelector).forEach((trackedNode) => {
    removeNode(trackedNode);
  });
}

function handleNodeAdded(node: Node) {
  if (node.nodeType !== 1) {
    return;
  }

  const element = node as HTMLElement;
  const addNode = (trackedNode: Element) => {
    if (!(trackedNode instanceof HTMLElement)) {
      return;
    }

    const skuParam = element.getAttribute("sku");
    const groupParam = element.getAttribute("group");
    const type = skuParam ? "sku" : "group";

    const settings = props.settings.summary.find((summary) => {
      return typeof summary.selector === 'string'
        && trackedNode.classList.contains(summary.selector);
    });

    if (!settings || (!skuParam && !groupParam) || !summaryList.value) {
      return;
    }

    if (summaryList.value[(skuParam || groupParam) as string]) {
      elementInit(trackedNode, settings, (skuParam || groupParam) as string, type);
    } else {
      waitList.value.push({ trackedNode, settings, idParam: (skuParam || groupParam) as string, type });
      debouncedGetSummary();
    }
  };

  if (element.nodeName.toLowerCase() === FRSelector) {
    addNode(element);
  }

  element.querySelectorAll(FRSelector).forEach((trackedNode) => {
    addNode(trackedNode);
  });
}

function elementInit(domElement: Element, settings: ISummary, idParam: string, type: string) {
  const isAlreadyTracked = items.value.some((item) => item.el === domElement);

  if (isAlreadyTracked) {
    return;
  }

  if (!summaryList.value || !summaryList.value[idParam] || isAlreadyTracked) {
    return;
  }

  items.value.push({ el: domElement, type: settings.view.type, settings, idParam, idType: type });
}

function init() {
  const elements = document.querySelectorAll(FRSelector);

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];

    if (!(element instanceof HTMLElement)) {
      continue;
    }

    const skuParam = element.getAttribute("sku");
    const groupParam = element.getAttribute("group");
    const type = skuParam ? "sku" : "group";

    const settings = props.settings.summary.find((summary) => {
      return typeof summary.selector === 'string'
        && element.classList.contains(summary.selector)
    });

    if (!settings || (!skuParam && !groupParam)) {
      continue;
    }

    elementInit(element, settings, (skuParam || groupParam) as string, type);
  }
}

init();
</script>

<style lang="scss">
body {
  --fr-color-white: #FFF;
  --fr-color-primary: #008ACB;
  --fr-text-color-primary: #303133;
  --fr-text-color-primary-light-1: #ecf5ff;
  --fr-text-color-primary-dark-1: #337ECC;
  --fr-text-color-primary-dark-2: #007DB7;
  --fr-color-success-light-1: #F0F9EB;
  --fr-text-color-secondary: #909399;
  --fr-text-color-success-dark-1: #529B2E;
  --fr-background-color-secondary: #ECF5FF;

  --fr-shadow-primary: 0px 0px 6px 0px #0000001F;

  --fr-faq-icon-color: #A8ABB2;

  --fr-popup-icon-primary: #00B9BF;

  --fr-logo-text-color: #6DC3F5;
  --fr-logo-icon-primary: #E8F4FB;
  --fr-logo-icon-secondary: #A9DAF5;

  --fr-stars-icon-primary: #DEF3F4;
  --fr-stars-icon-secondary: #E7FBFC;
  
  --fr-logo-icon-width: 67px;
  --fr-stars-icon-width: 48px;

  --fr-layout-gap-primary: 8px;
  --fr-tags-gap-primary: 8px;

  --fr-background-linear-primary: linear-gradient(265.8deg, #E7F5FF 19.68%, #E5ECFF 85.69%);
  --fr-background-linear-secondary: linear-gradient(265.8deg, #E7F5FF 19.68%, #E5ECFF 85.69%);
}

@import "../styles/FSumMain.scss";
</style>
